/**
 * Het ophalen en verversen van het rooster.
 *
 * Draagt de weerbaarheid uit docs/architectuur.md, laag 1: pollen op een vast
 * interval, exponentiële backoff bij netwerkfouten, en bij een fout blijft de
 * laatst bekende stand op het scherm staan in plaats van dat het leegloopt. Een
 * tablet die na een netwerkstoring terugkomt mag de API niet overspoelen.
 *
 * Waarom een eigen hook en geen data-bibliotheek: dit is één endpoint met één
 * pollritme. React Query zou hier meer configuratie en een extra dependency
 * kosten dan de vijftig regels die het vervangt.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { haalRooster } from '../api';
import { config } from '../config';
import { ApiFout, type Rooster } from '../types';

interface RoosterToestand {
  rooster: Rooster | null;
  /** Mislukte polls sinds de laatste geslaagde — voedt de backoff en de melding. */
  fouten: number;
  /** Melding van de laatste mislukte poging, of null als de laatste poging lukte. */
  netwerkMelding: string | null;
  /** Wanneer de volgende poging staat gepland, voor de voortgangsbalk. */
  volgendePoll: number;
}

const LEEG: RoosterToestand = {
  rooster: null,
  fouten: 0,
  netwerkMelding: null,
  volgendePoll: 0,
};

/** Wachttijd tot de volgende poging: normaal interval, of backoff na fouten. */
export function pollVertraging(fouten: number): number {
  if (fouten === 0) return config.pollIntervalMs;
  return Math.min(config.pollIntervalMs * 2 ** fouten, config.backoffMaxMs);
}

/**
 * @param leerlingnummer het ingelogde nummer, of null als er niemand ingelogd is
 * @param dag welke dag getoond wordt, 'YYYY-MM-DD'
 * @param onNummerOngeldig wordt geroepen als het nummer niet (meer) bestaat; de app
 *        hoort dan terug te gaan naar het inlogscherm in plaats van door te pollen
 * @param startRooster het al opgehaalde scanrooster; de volgende poll volgt na het interval
 */
export function useRooster(
  leerlingnummer: string | null,
  dag: string,
  onNummerOngeldig: (melding: string) => void,
  startRooster: Rooster | null = null,
) {
  const [toestand, setToestand] = useState<RoosterToestand>(LEEG);

  // In refs omdat de poll-lus ze moet kunnen lezen zonder zichzelf opnieuw op te
  // zetten: een callback in de dependencies zou bij elke render een nieuwe timer
  // starten en het pollritme onvoorspelbaar maken.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fouten = useRef(0);
  const ongeldigCallback = useRef(onNummerOngeldig);
  ongeldigCallback.current = onNummerOngeldig;

  useEffect(() => {
    if (!leerlingnummer) {
      setToestand(LEEG);
      fouten.current = 0;
      return;
    }

    // De scan heeft het rooster al opgehaald. Bij een andere dag laden we opnieuw.
    const beginRooster = startRooster?.dag === dag ? startRooster : null;
    setToestand((t) => ({ ...t, rooster: beginRooster }));

    const controller = new AbortController();
    let gestopt = false;

    const plan = () => {
      const vertraging = pollVertraging(fouten.current);
      setToestand((t) => ({ ...t, volgendePoll: Date.now() + vertraging }));
      timer.current = setTimeout(poll, vertraging);
    };

    const poll = async () => {
      if (gestopt) return;
      try {
        const rooster = await haalRooster(leerlingnummer, dag, controller.signal);
        if (gestopt) return;
        fouten.current = 0;
        setToestand((t) => ({ ...t, rooster, fouten: 0, netwerkMelding: null }));
      } catch (err) {
        if (gestopt || (err instanceof DOMException && err.name === 'AbortError')) return;

        if (err instanceof ApiFout && err.nummerOngeldig) {
          // Doorpollen op een nummer dat niet bestaat heeft geen zin.
          ongeldigCallback.current(err.message);
          return;
        }

        fouten.current += 1;
        const melding = err instanceof Error ? err.message : 'Onbekende fout';
        console.warn('Ophalen mislukt:', melding);
        // Bewust niet `rooster: null`: het laatst bekende rooster blijft staan.
        setToestand((t) => ({ ...t, fouten: fouten.current, netwerkMelding: melding }));
      }
      plan();
    };

    if (beginRooster) plan();
    else void poll();

    return () => {
      gestopt = true;
      controller.abort();
      if (timer.current) clearTimeout(timer.current);
    };
  }, [leerlingnummer, dag, startRooster]);

  /**
   * Nu verversen, buiten het pollritme om — voor de knop in het demo-paneel.
   *
   * Verzet de geplande poll niet: die mag op zijn eigen ritme blijven lopen. Eén
   * extra request is goedkoper dan een tweede plek die het pollritme aanpast.
   */
  const verversNu = useCallback(async () => {
    if (!leerlingnummer) return;
    try {
      const rooster = await haalRooster(leerlingnummer, dag);
      fouten.current = 0;
      setToestand((t) => ({ ...t, rooster, fouten: 0, netwerkMelding: null }));
    } catch (err) {
      const melding = err instanceof Error ? err.message : 'Onbekende fout';
      // Ook de ref bijwerken, anders loopt de backoff-teller uit de pas met wat
      // er op het scherm staat.
      fouten.current += 1;
      setToestand((t) => ({ ...t, netwerkMelding: melding, fouten: fouten.current }));
    }
  }, [leerlingnummer, dag]);

  return { ...toestand, verversNu };
}
